import { useState } from "react";

import { useTranslation } from "react-i18next";

import { ApiError } from "@/api/client";
import { useDeleteUser, type User } from "@/api/users";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Props {
  user: User | null;
  onClose: () => void;
}

export function DeleteUserDialog({ user, onClose }: Props) {
  const { t } = useTranslation();
  const del = useDeleteUser();
  const [serverError, setServerError] = useState<string | null>(null);

  if (!user) return null;

  const close = () => {
    setServerError(null);
    onClose();
  };

  const handleDelete = async () => {
    setServerError(null);
    try {
      await del.mutateAsync(user.id);
      close();
    } catch (err) {
      setServerError(
        (err instanceof ApiError && err.error?.message) ||
          t("settings.users.error"),
      );
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("settings.users.deleteTitle")}</DialogTitle>
          <DialogDescription>
            {t("settings.users.deleteConfirmation", { email: user.email })}
          </DialogDescription>
        </DialogHeader>
        {serverError && (
          <p className="text-destructive text-sm">{serverError}</p>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={close}>
            {t("common.buttons.cancel")}
          </Button>
          <Button
            variant="destructive"
            disabled={del.isPending}
            onClick={handleDelete}
          >
            {t("common.buttons.delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
